import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const CookiePolicy = () => {
  return (
    <>
      <SEOHead
        title="Cookie Policy - Smartborn"
        description="Cookie Policy for Smartborn - Learn about how we use cookies and similar technologies on our website."
        keywords="cookie policy, cookies, tracking, smartborn cookies"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <div className="max-w-4xl mx-auto">
            <h1 className="text-h1 mb-8">Cookie Policy</h1>
            
            <div className="prose prose-lg max-w-none space-y-8">
              <section>
                <p className="text-muted-foreground mb-6">
                  Last Updated: November 2024
                </p>
                <p className="text-body leading-relaxed">
                  This Cookie Policy explains how Smartborn ("we", "us", or "our") uses cookies and similar technologies 
                  on our website. By using our website, you consent to the use of cookies in accordance with this policy.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">What Are Cookies?</h2>
                <p className="text-body leading-relaxed">
                  Cookies are small text files that are placed on your device when you visit a website. They are widely used 
                  to make websites work more efficiently and provide information to website owners. Cookies can be "persistent" 
                  or "session" cookies. Persistent cookies remain on your device after you close your browser, while session 
                  cookies are deleted when you close your browser.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">How We Use Cookies</h2>
                <p className="text-body leading-relaxed mb-4">
                  We use cookies for the following purposes:
                </p>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-h3 mb-3">1. Necessary Cookies</h3>
                    <p className="text-body leading-relaxed mb-2">
                      These cookies are essential for the website to function properly. They enable basic functions like 
                      page navigation and access to secure areas of the website.
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-body">
                      <li>Session management</li>
                      <li>Security features</li>
                      <li>Cookie consent preferences</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-h3 mb-3">2. Analytics Cookies</h3>
                    <p className="text-body leading-relaxed mb-2">
                      These cookies help us understand how visitors interact with our website by collecting and reporting 
                      information anonymously. We use Google Analytics to track:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-body">
                      <li>Number of visitors</li>
                      <li>Pages visited and time spent on pages</li>
                      <li>Traffic sources</li>
                      <li>User behavior patterns</li>
                      <li>Scroll depth tracking</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-h3 mb-3">3. Functional Cookies</h3>
                    <p className="text-body leading-relaxed mb-2">
                      These cookies enable enhanced functionality and personalization, such as:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-body">
                      <li>Remembering your preferences</li>
                      <li>Language selection</li>
                      <li>Region selection</li>
                      <li>Video playback</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-h3 mb-3">4. Marketing Cookies</h3>
                    <p className="text-body leading-relaxed mb-2">
                      These cookies are used to track visitors across websites and display ads that are relevant and 
                      engaging for individual users:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-body">
                      <li>Targeted advertising</li>
                      <li>Ad campaign performance</li>
                      <li>Social media integration</li>
                      <li>Remarketing</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Third-Party Cookies</h2>
                <p className="text-body leading-relaxed mb-4">
                  We use services from third parties that may set cookies on your device:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h4 className="text-h4 mb-2">Google Analytics</h4>
                    <p className="text-body-sm leading-relaxed">
                      We use Google Analytics to analyze website traffic and usage patterns. Google Analytics uses cookies 
                      to collect information about your use of our website. For more information about Google Analytics cookies, 
                      visit <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" 
                      className="text-primary hover:text-primary/80">Google's Cookie Policy</a>.
                    </p>
                  </div>

                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h4 className="text-h4 mb-2">Social Media Platforms</h4>
                    <p className="text-body-sm leading-relaxed">
                      We integrate with social media platforms (Facebook, LinkedIn, Twitter, Instagram, YouTube) which may 
                      set their own cookies when you interact with our social media content or share our pages.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Managing Cookies</h2>
                <p className="text-body leading-relaxed mb-4">
                  You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences 
                  in several ways:
                </p>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-h3 mb-3">Cookie Consent Banner</h3>
                    <p className="text-body leading-relaxed">
                      When you first visit our website, you'll see a cookie consent banner where you can choose to accept all 
                      cookies, reject all non-essential cookies, or customize your preferences.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-h3 mb-3">Browser Settings</h3>
                    <p className="text-body leading-relaxed mb-2">
                      Most web browsers allow you to control cookies through their settings. To find out more about cookies, 
                      including how to see what cookies have been set and how to manage and delete them, visit:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-body">
                      <li><a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" 
                          className="text-primary hover:text-primary/80">www.allaboutcookies.org</a></li>
                      <li><a href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer" 
                          className="text-primary hover:text-primary/80">www.youronlinechoices.com</a></li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-h3 mb-3">Google Analytics Opt-Out</h3>
                    <p className="text-body leading-relaxed">
                      You can opt-out of Google Analytics tracking by installing the{" "}
                      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" 
                         className="text-primary hover:text-primary/80">
                        Google Analytics Opt-Out Browser Add-On
                      </a>.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg mt-4">
                  <p className="text-body-sm leading-relaxed">
                    <strong>Note:</strong> Blocking or deleting cookies may prevent you from using some features of our website 
                    and may affect your user experience.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Cookie Lifespan</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-border">
                    <thead>
                      <tr className="bg-muted/30">
                        <th className="border border-border p-3 text-left">Cookie Type</th>
                        <th className="border border-border p-3 text-left">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-border p-3">Session Cookies</td>
                        <td className="border border-border p-3">Deleted when browser is closed</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">Analytics Cookies</td>
                        <td className="border border-border p-3">Up to 2 years</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">Preference Cookies</td>
                        <td className="border border-border p-3">Up to 1 year</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">Marketing Cookies</td>
                        <td className="border border-border p-3">Up to 2 years</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Updates to This Cookie Policy</h2>
                <p className="text-body leading-relaxed">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, 
                  legal, or regulatory reasons. Please revisit this Cookie Policy regularly to stay informed about our use of cookies.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">More Information</h2>
                <p className="text-body leading-relaxed mb-4">
                  For more information about how we process your personal data, please see our{" "}
                  <Link to="/privacy-policy" className="text-primary hover:text-primary/80">Privacy Policy</Link>.
                </p>
                <p className="text-body leading-relaxed mb-4">
                  If you have any questions about our use of cookies, please contact us:
                </p>
                <div className="bg-muted/30 p-6 rounded-lg space-y-2">
                  <p className="text-body"><strong>Email:</strong> contact@smartborn.io</p>
                  <p className="text-body"><strong>Phone:</strong> +91 98869 28201</p>
                  <p className="text-body"><strong>Address:</strong> Bangalore, India</p>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CookiePolicy;